import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Flame, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { portalCatalogAPI } from '../../lib/portalApi';
import AddToCart from './AddToCart';
import ProductImage from '../ProductImage';

/**
 * Horizontal product reel used on the catalog hero area.
 *
 * Two variants — "new arrivals" and "best sellers" — surface as
 * sibling reels above the main grid. The reel auto-scrolls (slow,
 * paused on hover) and wraps around the end so the carousel always
 * stays in motion. Manual prev/next buttons still snap the user a
 * full viewport at a time, and pointer drag is preserved.
 *
 * Container is fully fluid — it consumes the available column width
 * and never causes the catalog page itself to scroll horizontally.
 */

const REEL_CONFIG = {
    new: {
        accent: 'text-emerald-700',
        Icon: Sparkles,
    },
    bestseller: {
        accent: 'text-[#C08478]',
        Icon: Flame,
    },
};

const StockDot = ({ status }) => {
    const dot = ({
        in_stock: 'bg-emerald-500',
        low: 'bg-amber-500',
        out: 'bg-red-500',
    })[status] || 'bg-gray-300';
    return <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />;
};

const ReelCard = ({ p, soldQty, onOpen }) => {
    const { t } = useTranslation('portal');
    return (
    <div
        data-testid={`reel-card-${p.sku}`}
        role="button"
        tabIndex={0}
        onClick={() => onOpen && onOpen(p.sku)}
        onKeyDown={(e) => { if (onOpen && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(p.sku); } }}
        // `w-[44%]` on mobile so 2 cards peek, then progressively
        // larger fixed widths on bigger screens for nicer rhythm.
        className="snap-start flex-shrink-0 w-[46%] xs:w-[42%] sm:w-[220px] md:w-[230px] lg:w-[220px] xl:w-[240px] bg-white border border-[#EFE5E1] rounded-xl overflow-hidden hover:shadow-md transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#C08478]/40"
    >
        <div className="aspect-square bg-gradient-to-br from-[#FBF8F6] to-[#F5EEEA] flex items-center justify-center overflow-hidden relative">
            <ProductImage product={p} testid={`reel-card-${p.sku}-img`} />
            {soldQty != null && (
                <span className="absolute top-2 left-2 bg-[#1A1A1A] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                    {t('reels.sold', { count: Math.round(soldQty) })}
                </span>
            )}
        </div>
        <div className="p-3 space-y-1.5">
            <div className="flex items-center gap-2 text-[10px]">
                <StockDot status={p.stock_status} />
                {p.brand && (
                    <span className="uppercase tracking-[2px] text-[#C08478] font-semibold truncate">{p.brand}</span>
                )}
            </div>
            <h4 className="text-[13px] font-medium text-[#1A1A1A] line-clamp-2 min-h-[34px]" title={p.name}>{p.name}</h4>
            <div className="flex items-center justify-between pt-1.5 border-t border-[#F5EEEA]">
                <span className="text-[10px] text-gray-400 font-mono">{p.sku}</span>
                {p.price_hidden ? (
                    <span className="text-[10px] italic text-[#C08478]">{t('catalog.price_on_request')}</span>
                ) : (
                    <span className="text-sm font-bold text-[#1A1A1A]">€{Number(p.price).toFixed(2)}</span>
                )}
            </div>
            {/* Prevent AddToCart clicks from bubbling into the reel card's open-quickview handler */}
            <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <AddToCart product={p} variant="card" />
            </div>
        </div>
    </div>
    );
};

const Reel = ({ kind, items, onOpen }) => {
    const { t } = useTranslation('portal');
    const cfg = REEL_CONFIG[kind];
    const scrollerRef = useRef(null);
    const [paused, setPaused] = useState(false);
    // `pausedRef` lets the rAF loop read the live value WITHOUT waiting
    // for the React render — critical so a click-to-pause takes effect
    // on the very next animation frame and the smooth-scroll isn't
    // overwritten by the loop one tick later.
    const pausedRef = useRef(false);

    useEffect(() => { pausedRef.current = paused; }, [paused]);

    // Auto-scroll loop — ~40px per second so it stays subtle. Resets
    // to the start when we hit the end, giving a clean wrap.
    useEffect(() => {
        const el = scrollerRef.current;
        if (!el || (items?.length || 0) < 2) return;
        let raf;
        let last = performance.now();
        const tick = (now) => {
            const dt = now - last;
            last = now;
            if (!pausedRef.current && el.scrollWidth > el.clientWidth) {
                el.scrollLeft += (dt / 1000) * 40;
                if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 1) {
                    el.scrollLeft = 0;
                }
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [items]);

    if (!items || items.length === 0) return null;

    const scrollByPage = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        // Pause immediately via ref + state. State drives the hover-pause
        // class hooks; the ref prevents the in-flight rAF tick from
        // stomping on our smooth scroll.
        pausedRef.current = true;
        setPaused(true);
        el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
        window.clearTimeout(scrollByPage._t);
        scrollByPage._t = window.setTimeout(() => {
            pausedRef.current = false;
            setPaused(false);
        }, 1500);
    };

    const Icon = cfg.Icon;
    const title = kind === 'new' ? t('reels.new_arrivals') : t('reels.best_sellers');
    return (
        <section className="space-y-3 min-w-0" data-testid={`reel-${kind}`}>
            <div className="flex items-center justify-between gap-3">
                <h2 className={`flex items-center gap-2 text-sm uppercase tracking-[3px] font-semibold ${cfg.accent}`}>
                    <Icon size={14} /> {title}
                </h2>
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={() => scrollByPage(-1)}
                        aria-label={t('reels.scroll_left')}
                        data-testid={`reel-${kind}-prev`}
                        className="w-8 h-8 rounded-full bg-white border border-[#EFE5E1] flex items-center justify-center text-gray-500 hover:text-[#1A1A1A] hover:bg-[#F5EEEA]"
                    >
                        <ChevronLeft size={14} />
                    </button>
                    <button
                        onClick={() => scrollByPage(1)}
                        aria-label={t('reels.scroll_right')}
                        data-testid={`reel-${kind}-next`}
                        className="w-8 h-8 rounded-full bg-white border border-[#EFE5E1] flex items-center justify-center text-gray-500 hover:text-[#1A1A1A] hover:bg-[#F5EEEA]"
                    >
                        <ChevronRight size={14} />
                    </button>
                </div>
            </div>
            <div
                ref={scrollerRef}
                onMouseEnter={() => { pausedRef.current = true; setPaused(true); }}
                onMouseLeave={() => { pausedRef.current = false; setPaused(false); }}
                onTouchStart={() => { pausedRef.current = true; setPaused(true); }}
                onTouchEnd={() => { pausedRef.current = false; setPaused(false); }}
                className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory pb-2 scroll-smooth scrollbar-thin"
                style={{ scrollbarWidth: 'thin' }}
            >
                {/* Duplicate the items so the loop has something to wrap into
                    cleanly — keeps the rhythm steady on slow connections. */}
                {[...items, ...items].map((p, idx) => (
                    <ReelCard key={`${kind}-${p.id}-${idx}`} p={p} soldQty={kind === 'bestseller' ? p.qty_sold : null} onOpen={onOpen} />
                ))}
            </div>
        </section>
    );
};

export const CatalogReels = ({ onOpen }) => {
    const [data, setData] = useState({ new_arrivals: [], best_sellers: [] });
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let mounted = true;
        portalCatalogAPI.featured({ limit: 10 })
            .then(({ data: d }) => { if (mounted) setData(d); })
            .catch(() => { /* silently skip — reels are progressive enhancement */ })
            .finally(() => { if (mounted) setLoaded(true); });
        return () => { mounted = false; };
    }, []);

    if (!loaded) return null;
    const hasNew = (data.new_arrivals || []).length > 0;
    const hasBest = (data.best_sellers || []).length > 0;
    if (!hasNew && !hasBest) return null;

    return (
        <div className="space-y-6 min-w-0" data-testid="catalog-reels">
            <Reel kind="new" items={data.new_arrivals} onOpen={onOpen} />
            <Reel kind="bestseller" items={data.best_sellers} onOpen={onOpen} />
        </div>
    );
};

export default CatalogReels;
